import React, { useEffect, useState } from 'react'
import { Card, Carousel } from '@material-tailwind/react'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import { GatsbyImage, type IGatsbyImageData } from 'gatsby-plugin-image'
import { cx } from 'class-variance-authority'
import { Media } from './Media'
import { isPortaitPicture } from './utils'
import { YoutubeVideo } from './YoutubeVideo'
import { mediaType } from '../../firms/firm/mapper'

export interface MediaInterface extends mediaType {
  name: string
  youTubeApiKey?: string | null
}

export interface ModalWithMediasProps extends MediaInterface {
  otherMedias?: (MediaInterface & { id: string })[]
  forceHeight?: boolean
}

export const ModalWithMedias = ({
  id: mediaId,
  media,
  name,
  video,
  youTubeApiKey,
  otherMedias = [],
  forceHeight = false,
}: ModalWithMediasProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [carouselHeight, setCarouselHeight] = useState<undefined | number>(
    undefined,
  )
  const handleOpen = () => setIsOpen((cur) => !cur)
  const carouselMedias = reorderMedias().filter(Boolean)

  /**
   * Reorder the medias to display the selected media first
   */
  function reorderMedias() {
    if (otherMedias.length === 0) {
      return []
    }
    if (video) {
      const index = otherMedias.findIndex(
        (listItem) => listItem.media === media && listItem.video,
      )
      const medias = [...otherMedias]
      const [firstMedia] = medias.splice(index, 1)
      medias.unshift(firstMedia)
      return medias
    }
    const index = otherMedias.findIndex(({ id }) => id === mediaId)
    const medias = [...otherMedias]
    const [firstMedia] = medias.splice(index, 1)
    medias.unshift(firstMedia)
    return medias
  }

  useEffect(() => {
    if (!carouselHeight) {
      const el = document.getElementById('carousel')
      if (el) {
        setCarouselHeight(el.getBoundingClientRect()?.height)
      }
    }
  })

  return (
    <>
      <Card
        id={`card-media-${mediaId}`}
        className="h-full cursor-pointer rounded-xl"
        onClick={handleOpen}
      >
        <Media
          id={mediaId}
          media={media}
          name={name}
          video={video}
          youTubeApiKey={youTubeApiKey}
          forceHeight={forceHeight}
        />
      </Card>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <DialogBackdrop className="fixed inset-0 bg-black/60" />

        {/* Full-screen container to center the panel */}
        <div className="fixed inset-0 flex items-center justify-center w-screen">
          {/* The actual dialog panel  */}
          <DialogPanel
            id={!carouselMedias.length ? mediaId || undefined : 'carousel'}
            className="max-w-6xl"
          >
            {carouselMedias.length > 0 ? (
              <Carousel id="carousel" navigation={() => null}>
                {carouselMedias.map((item) => {
                  const med = item?.media as IGatsbyImageData
                  if (item.video) {
                    return (
                      <div
                        key={item.id}
                        className="w-full h-full max-w-6xl mx-auto"
                      >
                        <YoutubeVideo
                          title={`vidéo de ${name}`}
                          url={med as unknown as string}
                          height={carouselHeight || 600}
                        />
                      </div>
                    )
                  }

                  return (
                    <div
                      className="h-full max-w-6xl mx-auto max-h-[80vh] overflow-y-hidden rounded-xl flex items-center justify-center"
                      key={item.id}
                    >
                      <div className="rounded-xl">
                        <DisplayImage item={item} name={name} />
                      </div>
                    </div>
                  )
                })}
              </Carousel>
            ) : (
              <div className="max-h-[80vh] overflow-hidden rounded-xl flex items-center justify-center">
                <Media
                  id={mediaId}
                  media={media}
                  name={name}
                  video={video}
                  youTubeApiKey={youTubeApiKey}
                  isIFrame={Boolean(video)}
                />
              </div>
            )}
          </DialogPanel>
        </div>
      </Dialog>
    </>
  )
}

const DisplayImage = ({
  item,
  name,
}: {
  item?: MediaInterface & { id: string }
  name: string
}) => {
  if (!item) {
    return null
  }
  if (typeof item.media === 'string' || item.local?.url) {
    return (
      <img
        src={typeof item.media === 'string' ? item?.media : item.local?.url}
        className={cx('max-h-[80vh] rounded-xl')}
        alt={`Cabinet ${name}`}
      />
    )
  }
  if (typeof item.media !== 'string') {
    const med = item?.media as IGatsbyImageData
    return (
      <GatsbyImage
        id={item.id}
        image={{ ...med, height: med?.height } as unknown as IGatsbyImageData}
        alt={`image de ${name}`}
        className="object-cover max-h-[80vh] rounded-xl grid place-items-center"
        imgStyle={{
          borderRadius: '0.75rem',
          overflowY: 'visible',
          height: isPortaitPicture(med) ? 'auto' : '100%',
        }}
      />
    )
  }
  return null
}
